import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import createSlug from "../../createSlug";

const BlogCards = ({ item }) => {
  const navigate = useNavigate();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  const slug = useMemo(() => createSlug(item.title), [item.title]);
  const { title, imageUrls, date, content, index, author } = item;

  return (
    <div
      className="group cursor-pointer h-full"
      onClick={() => navigate(`/blogs/${index}/${slug}`)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <article className="bg-white rounded-lg sm:rounded-xl overflow-hidden shadow-[rgba(0,0,0,0.08)_0px_4px_20px_0px] hover:shadow-[rgba(0,0,0,0.25)_0px_20px_40px_0px] transition-all duration-500 ease-out transform sm:hover:scale-[1.02] sm:hover:-translate-y-2 h-full lg:h-[36rem] md:h-[33rem] sm:h-auto flex flex-col">
        
        {/* Enhanced Image Section */}
        <div className="relative h-[200px] xs:h-[220px] sm:h-[240px] md:h-[260px] lg:h-[18rem] w-full overflow-hidden">
          <div className="relative h-full w-full">
            <img
              loading="lazy"
              src={imageUrls}
              alt="Akiya 2.0 blogs"
              className={`object-cover w-full h-full transition-all duration-700 ease-out ${
                isHovered 
                  ? 'sm:scale-110 sm:brightness-110' 
                  : 'scale-100 brightness-100'
              }`}
              onLoad={() => setImageLoaded(true)}
            />
            
            {/* Loading shimmer effect */}
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite]">
                <div className="absolute inset-0 bg-gray-200/50"></div>
              </div>
            )}
            
            {/* Hover overlay */}
            <div className={`absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent transition-all duration-500 ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}></div>
            
            {/* Author badge - responsive positioning and sizing */}
            <div className={`absolute top-2 left-2 sm:top-4 sm:left-4 bg-white/90 backdrop-blur-sm text-[#10c9c0] px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-semibold shadow-lg transition-all duration-500 ${
              isHovered ? 'sm:scale-105 bg-white' : 'scale-100'
            }`}>
              <span className=" sm:hidden">{author.split(' ')[0]}</span>
              <span className="hidden sm:block">{author}</span>
            </div>
            
            {/* Date badge - responsive positioning and sizing */}
            <div className={`absolute top-2 right-2 sm:top-4 sm:right-4 bg-[#10c9c0]/90 backdrop-blur-sm text-white px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg text-xs sm:text-sm font-semibold shadow-lg transition-all duration-500 ${
              isHovered ? 'sm:scale-105 sm:bg-[#0eb1a9]' : 'scale-100'
            }`}>
              {date}
            </div>
          </div>
        </div>

        {/* Enhanced Content Section */}
        <div className="flex-1 flex flex-col p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
          {/* Title */}
          <h2 className={`text-center leading-tight sm:leading-[36px] text-lg sm:text-xl md:text-[24px] font-[800] text-gray-900 line-clamp-2 sm:line-clamp-3 transition-all duration-500 ${
            isHovered 
              ? 'sm:text-[#10c9c0] sm:transform sm:translate-y-[-2px]' 
              : 'text-gray-900 transform translate-y-0'
          }`}>
            {title}
          </h2>
          
          {/* Content */}
          <p className={`text-[#949995] text-sm sm:text-base md:text-[16px] font-[300] leading-relaxed sm:leading-[24px] flex-1 line-clamp-3 sm:line-clamp-4 transition-all duration-500 delay-75 ${
            isHovered 
              ? 'sm:text-[#737873] sm:transform sm:translate-x-1' 
              : 'text-[#949995] transform translate-x-0'
          }`}>
            {content}...
          </p>
          
          {/* Read More Indicator */}
          <div className={`flex items-center justify-center gap-2 text-[#10c9c0] font-semibold text-sm transition-all duration-300 mt-auto pt-2 ${
            isHovered ? 'sm:translate-y-[-2px]' : 'translate-y-0'
          }`}>
            <span className="hidden sm:inline">Read Full Article</span>
            <span className="sm:hidden">Read More</span>
            <svg 
              className={`w-4 h-4 transition-all duration-300 ${
                isHovered ? 'sm:translate-x-1' : 'translate-x-0'
              }`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>
        </div>
        
        {/* Subtle border accent */}
        <div className={`absolute inset-0 rounded-xl border-2 transition-all duration-500 pointer-events-none ${
          isHovered 
            ? 'border-[#10c9c0]/30' 
            : 'border-transparent'
        }`}></div>
        
        {/* Bottom accent line */}
        <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] transition-all duration-500 ${
          isHovered ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
        } origin-center`}></div>
      </article>
      
      {/* Custom CSS for shimmer animation */}
      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>
  );
};

export default BlogCards;